import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/ui/Sidebar";
import PageHeader from "../components/ui/PageHeader";
import TextInputComponent from "../components/ui/TextInputComponent";
import SelectInputComponent from "../components/ui/SelectInputComponent";
import ButtonComponent from "../components/ui/ButtonComponent";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { auth } from "../firebase-config";
import ModalComponent from "../components/ui/ModalComponent";
import BasicInfoStep from "../components/events/steps/BasicInfoStep";
import LogisticsStep from "../components/events/steps/LogisticsStep";
import MarketingContactStep from "../components/events/steps/MarketingContactStep";
import AdvancedStep from "../components/events/steps/AdvancedStep";
import RsvpSettingsStep from "../components/events/steps/RsvpSettingsStep";
import EventFormStepperComponent from "../components/ui/EventFormStepperComponent";
import { getFunctions, httpsCallable } from "firebase/functions";
import { functions } from "../firebase-config"; 
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import axios from "axios";
import { useToast } from "../components/ui/ToastContext";
import GooglePlacesInputComponent from "../components/ui/GooglePlacesInputComponent";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

// Define RSVP defaults once for reuse across create + edit
const DEFAULT_RSVP_SETTINGS = {
  collectEmail: true,
  collectPhone: false,
  collectPartySize: true,
  collectAdultsKids: true,
  collectKidsAges: false,
  collectGender: false,
  collectNotes: true,
  customQuestions: [],
};

// Define it once for reuse
const emptyFormState = {
    eventType: "",
    eventId: "",
    eventName: "",
    fundraisingGoal: "",
    location: "",
    locationPlaceId: "",
    locationLat: null,
    locationLng: null,
    startDate: null,
    startTime: null,
    budget: "",
    targetAudience: [],
    registrationDeadline: null,
    maxAttendees: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    internalNotes: "",
    allowRsvps: "",
    tags: "",
    visibleToPublic: "",
    description: "",
    eventImageUrl: "",
    budgetData: null,
    estimatedCost: 0,
    rsvpSettings: {
      ...DEFAULT_RSVP_SETTINGS,
    },
  };

const ManageEvents = () => {
  const [formData, setFormData] = useState({ ...emptyFormState });
  const [eventOptions, setEventOptions] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [spreadsheetError, setSpreadsheetError] = useState(null);
  const [errors, setErrors] = useState({});
  const [loadingDescription, setLoadingDescription] = useState(false);
  const [showMicrositePrompt, setShowMicrositePrompt] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [createdEventId, setCreatedEventId] = useState(null);
  
  const { addToast } = useToast();
  const navigate = useNavigate();

  const steps = [
    { label: "Basic Info", component: BasicInfoStep },
    { label: "Logistics", component: LogisticsStep },
    { label: "RSVP Settings", component: RsvpSettingsStep }, // new
    { label: "Marketing & Contact", component: MarketingContactStep },
    { label: "Advanced", component: AdvancedStep },
  ];

  const handlePlaceSelect = (place) => {
    setFormData((prev) => ({
      ...prev,
      location: place.address,
      locationLat: place.lat,
      locationLng: place.lng,
      locationPlaceId: place.placeId,
      locationDetails: place, 
    }));
  };

  const fetchEvents = async () => {
    try {
      if (!auth.currentUser) {
        console.warn("No authenticated user yet. Skipping fetch.");
        return;
      }
  
      const idToken = await auth.currentUser.getIdToken();
      const orgId = sessionStorage.getItem("organizationId");
  
      const res = await axios.get(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/getEvent",
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "organization-id": orgId,
          },
        }
      );
  
      if (res.data.events) {
        const nonCompleted = res.data.events.filter(
          (event) => event.eventStatus !== "Completed"
        );
  
        const formatted = nonCompleted.map((event) => ({
          ...event,
          label: `${event.eventName} (${event.eventId})`,
        }));
  
        setEventOptions(formatted);
      }
    } catch (err) {
      console.error("Fetch events error:", err);
      addToast("Failed to fetch events", "error");
    }
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        fetchEvents();
      } else {
        console.warn("User not authenticated, skipping fetchEvents");
      }
    });
  
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (createdEventId) {
      console.log("Created Event ID ready:", createdEventId);
    }
  }, [createdEventId]);

  const handleChange = (field) => (e) => {
    let value = e.target.value;
  
    if (field === "targetAudience" && !Array.isArray(value)) {
      value = [value];
    }
  
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEventSelection = async (event, newValue) => {
    setSelectedEvent(newValue);
  
    if (newValue) {
      const populatedData = {
        ...emptyFormState,
        ...newValue,
        startDate: newValue.startDate ? dayjs(newValue.startDate) : null,
        startTime: newValue.startTime ? dayjs(newValue.startTime) : null,
        registrationDeadline: newValue.registrationDeadline
          ? dayjs(newValue.registrationDeadline)
          : null,
  
        // Flatten nested contact
        contactName: newValue?.contact?.name || "",
        contactEmail: newValue?.contact?.email || "",
        contactPhone: newValue?.contact?.phone || "",
  
        // Normalize fields
        description: newValue.description || "",
        tags: Array.isArray(newValue.eventTags)
          ? newValue.eventTags.join(", ")
          : newValue.tags || "",
  
        visibleToPublic: newValue.visibleToPublic ?? false,
        allowRsvps: newValue.allowRsvps ?? true,
        eventType: newValue.eventType || "",

        // Always merge RSVP defaults with saved event settings
        rsvpSettings: {
          ...DEFAULT_RSVP_SETTINGS,
          ...(newValue.rsvpSettings || {}),
        },
      };
  
      // Geocoding if no structured location
      if (!newValue.locationDetails && newValue.location) {
        try {
          const geocoder = new window.google.maps.Geocoder();
          geocoder.geocode({ address: newValue.location }, (results, status) => {
            if (status === "OK" && results[0]) {
              const { formatted_address, geometry, place_id } = results[0];
              const lat = geometry.location.lat();
              const lng = geometry.location.lng();
  
              populatedData.location = formatted_address;
              populatedData.locationDetails = {
                address: formatted_address,
                lat,
                lng,
                placeId: place_id,
              };
              populatedData.locationLat = lat;
              populatedData.locationLng = lng;
              populatedData.locationPlaceId = place_id;
  
              setFormData(populatedData);
            } else {
              setFormData(populatedData);
            }
          });
        } catch (error) {
          console.error("Geocoder failed:", error);
          setFormData(populatedData);
        }
      } else {
        setFormData(populatedData);
      }
  
      setIsEditMode(true);
  
      if (newValue.eventImage || newValue.eventImageUrl) {
        setBannerPreview(newValue.eventImage || newValue.eventImageUrl);
      }
    }
  };

  const handleClear = () => {
    setFormData({ ...emptyFormState });
    setIsEditMode(false);
    setSelectedEvent(null);
    setBannerPreview(null);
    setActiveStep(0);
    setCreatedEventId(null);
  };

  const validateFields = () => {
    const requiredFields = ["eventType", "eventId", "eventName", "startDate", "startTime", "location"];
    const newErrors = {};
    requiredFields.forEach((field) => {
      if (!formData[field]) newErrors[field] = "Required";
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGenerateDescription = async () => {
    setLoadingDescription(true);
    try {
      const {
        eventType,
        eventName,
        fundraisingGoal,
        location,
        startDate,
        targetAudience,
        description,
        userEmail,
        organizationId,
      } = formData;
  
      const response = await axios.post(
        "https://generateeventdescription-774073159618.us-central1.run.app",
        {
          eventType,
          eventName,
          fundraisingGoal,
          location,
          startDate,
          targetAudience,
          userDescription: description,
          userEmail,
          organizationId,
        }
      );
  
      setFormData((prev) => ({
        ...prev,
        description: response.data.description,
      }));
    } catch (error) {
      console.error("Kinova AI Error:", error);
      addToast("Error generating event description.", "error");
    } finally {
      setLoadingDescription(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateFields()) {
      addToast("Please fill in all required fields.", "error");
      return;
    }
  
    const user = auth.currentUser;
    if (!user) {
      addToast("You must be logged in to submit an event.", "error");
      return;
    }
  
    try {
      const idToken = await user.getIdToken(true);
      const orgId = sessionStorage.getItem("organizationId");
  
      let base64Banner = null;
      let bannerMimeType = null;
  
      if (formData.bannerFile) {
        bannerMimeType = formData.bannerFile.type || "image/jpeg";
        base64Banner = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.onerror = reject;
          reader.readAsDataURL(formData.bannerFile);
        });
      }
  
      const formattedData = {
        ...formData,
        eventTags: formData.tags
          ? formData.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
          : [],
        tags: undefined,
        startDate: formData.startDate ? formData.startDate.toISOString() : null,
        startTime: formData.startTime ? formData.startTime.toISOString() : null,
        registrationDeadline: formData.registrationDeadline
          ? formData.registrationDeadline.toISOString()
          : null,
        bannerFileBase64: base64Banner,
        bannerFileName: formData.bannerFile?.name || null,
        bannerMimeType,
        locationLat: formData.locationLat || null,
        locationLng: formData.locationLng || null,
        locationPlaceId: formData.locationPlaceId || null,
      };
  
      const endpoint = isEditMode
        ? "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/updateEvent"
        : "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/addEvent";
  
      const method = isEditMode ? "put" : "post";
  
      const response = await axios[method](endpoint, formattedData, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          "organization-id": orgId,
        },
      });
  
      const finalEventId = response.data?.eventId || formData.eventId;
      setFormData((prev) => ({ ...prev, eventId: finalEventId }));
      setCreatedEventId(finalEventId); // persist the event ID here
  
      addToast(`Event ${isEditMode ? "updated" : "created"} successfully`, "success");
  
      if (!isEditMode) {
        setShowMicrositePrompt(true); // trigger modal before clearing state
      }
  
      await refreshEventList();

    } catch (err) {
      console.error("Event submit failed:", err);
      addToast("Failed to submit event.", "error");
    }
  };

  const handleFileChange = (e) => {
    console.log("File input changed");
    const file = e.target.files[0];
    if (!file) return;
  
    setBannerPreview(URL.createObjectURL(file));
    setFormData((prev) => ({
      ...prev,
      bannerFile: file,
    }));
  
    addToast("Banner selected. It will be uploaded once the event is created.", "info");
  };

  const handleBudgetFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const sheetData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      let totalCost = 0;
      sheetData.forEach((row) => {
        const amount = parseFloat(row[1]);
        if (!isNaN(amount)) {
          totalCost += amount;
        }
      });

      setFormData((prev) => ({
        ...prev,
        budgetData: sheetData,
        estimatedCost: totalCost.toFixed(2),
      }));
      setSpreadsheetError(null);
    };

    if (file.name.endsWith(".csv") || file.name.endsWith(".xlsx")) {
      reader.readAsArrayBuffer(file);
    } else {
      setSpreadsheetError("Unsupported file format. Please upload .csv or .xlsx");
    }
  };

  const handleHeaderSearch = (searchTerm) => {
    setSearchInput(searchTerm);
  
    if (!searchTerm.trim()) {
      setSelectedEvent(null);
      setIsEditMode(false);
      setFormData({ ...emptyFormState });
      return;
    }
  
    const match = eventOptions.find((event) =>
      event.eventName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  
    if (match) {
      handleEventSelection(null, match);
    } else {
      setSelectedEvent(null);
      setIsEditMode(false);
      setFormData({ ...emptyFormState });
    }
  };
  
  const handleDeleteEvent = async () => {
    try {
      if (!auth.currentUser) {
        addToast("You must be logged in to delete an event.", "error");
        return;
      }
  
      const idToken = await auth.currentUser.getIdToken(true);
      const organizationId = sessionStorage.getItem("organizationId");
  
      await axios.put(
        "https://us-central1-integrity-resolutions-inc.cloudfunctions.net/deleteEvent",
        {
          eventId: formData.eventId,
          organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
  
      addToast("Event deleted successfully", "success");
      await refreshEventList();
      handleClear();
      setShowDeleteDialog(false);
    } catch (err) {
      console.error("Delete error:", err);
      addToast("Failed to delete event", "error");
    }
  };

  const refreshEventList = async () => {
    await fetchEvents();
  };

  // Determine whether this event already has microsite config saved
    const hasMicrositeConfig =
      !!selectedEvent?.micrositeConfig ||
      !!formData?.micrositeConfig ||
      !!formData?.micrositeConfig?.backgroundImageUrl ||
      !!formData?.backgroundImageUrl;

    // We only show "View Live Microsite" if published
    const isPublished = formData?.visibleToPublic === true || selectedEvent?.visibleToPublic === true;

    // Build the public microsite URL (matches MicrositePage route)
    const getLiveMicrositeUrl = () => {
      const orgId = sessionStorage.getItem("organizationId");
      const eId = formData.eventId || selectedEvent?.eventId;
      if (!orgId || !eId) return "";
      return `https://app.kinovacrm.com/${orgId}/${eId}`;
    };
  
  return (
    <div className="manage-users-container">
      <Sidebar />
      <div className="manage-users-content">
        <PageHeader
          title="Manage Events"
          showSearch={true}
          onSearch={handleHeaderSearch}
          actions={[
            // Edit/Create Microsite button
            formData.eventId && (
              <ButtonComponent
                key="edit-microsite"
                label={hasMicrositeConfig ? "Edit Microsite" : "Create Microsite"}
                type="secondary"
                onClick={() => {
                  navigate(`/microsite-builder/${formData.eventId}`);
                }}
              />
            ),

            // View Live Microsite button (only when published)
            formData.eventId && isPublished && (
              <ButtonComponent
                key="view-live-microsite"
                label="View Live Microsite"
                variant="outlined"
                type="primary"
                onClick={() => {
                  const liveUrl = getLiveMicrositeUrl();
                  if (liveUrl) window.open(liveUrl, "_blank", "noopener,noreferrer");
                  else addToast("Missing org or event ID for live URL.", "error");
                }}
              />
            ),

            // Delete Event button
            formData.eventId && (
              <ButtonComponent
                key="delete-event"
                label="Delete Event"
                variant="outlined"
                type="danger"
                onClick={() => setShowDeleteDialog(true)}
              />
            ),

            // Clear Form button
            <ButtonComponent
              key="clear-form"
              label="Clear Form"
              type="primary"
              onClick={handleClear}
            />,
          ]}
        />

        <Box mt={2} mb={2} display="flex" justifyContent="flex-end">
            <Box sx={{ width: "300px" }}>
              <SelectInputComponent
                label="Select Existing Event"
                value={selectedEvent?.eventId || ""}
                onChange={(e) => {
                  const selected = eventOptions.find(
                    (event) => event.eventId === e.target.value
                  );
                  handleEventSelection(null, selected);
                }}
                options={[
                  { label: "", value: "" },
                  ...eventOptions.map((event) => ({
                    label: event.label,
                    value: event.eventId,
                  })),
                ]}
              />
            </Box>
          </Box>

        <Box sx={{ width: "100%", mt: 2 }}>
        <EventFormStepperComponent
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
            handleSubmit={handleSubmit}
            loadingDescription={loadingDescription}
            handleGenerateDescription={handleGenerateDescription}
            bannerPreview={bannerPreview}
            uploadingBanner={uploadingBanner}
            handleFileChange={handleFileChange}
            handleChange={handleChange}
            spreadsheetError={spreadsheetError}
            handleBudgetFileUpload={handleBudgetFileUpload}
            handleClear={handleClear}
            isEditMode={isEditMode}
            setShowDeleteDialog={setShowDeleteDialog}
            showMicrositePrompt={showMicrositePrompt}
            setShowMicrositePrompt={setShowMicrositePrompt}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            handlePlaceSelect={handlePlaceSelect}
            />
        </Box>

        {bannerPreview && (
            <Box mt={2} mb={2}>
              <Typography variant="h6">Banner Preview:</Typography>
              <Box
                component="img"
                src={bannerPreview}
                alt="Event Banner Preview"
                sx={{
                  width: "100%",
                  maxHeight: 300,
                  objectFit: "cover",
                  borderRadius: 2,
                  mt: 1,
                }}
              />
            </Box>
          )}
           {/* Modals and Dialogs */}
           <ModalComponent
            open={showDeleteDialog}
            title="Delete Event"
            content={`Are you sure you want to delete the event "${formData.eventName}"? This action cannot be undone.`}
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={handleDeleteEvent}
            onCancel={() => setShowDeleteDialog(false)}
            isLoading={isDeleting}
            showCancel
          />
  
          <Dialog open={showMicrositePrompt} onClose={() => setShowMicrositePrompt(false)}>
          <DialogTitle>Create Microsite?</DialogTitle>
          <DialogContent>
            Would you like to create a website/microsite for this event?
          </DialogContent>
          <DialogActions>
            <ButtonComponent
              label="Create Later"
              onClick={() => {
                setShowMicrositePrompt(false);
                handleClear(); 
              }}
            />
            <ButtonComponent
              label="Let Kinova Create It"
              type="primary"
              onClick={async () => {
                setShowMicrositePrompt(false);
                console.log("Auto-generating microsite for event:", createdEventId || formData.eventId);
                handleClear(); 
              }}
            />
            <ButtonComponent
              label="Create Microsite"
              type="primary"
              onClick={() => {
                const finalId = createdEventId || formData.eventId;
                if (!finalId) {
                  addToast("Missing event ID. Please try again.", "error");
                  return;
                }
                setShowMicrositePrompt(false);
                navigate(`/microsite-builder/${finalId}`);
                handleClear(); 
              }}
            />
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
            
};

export default ManageEvents;